import classNames from 'classnames';
import * as _ from 'lodash';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import * as api from '../api/api';
import spaceLogo from '../assets/img/space-usage/logo.png';
import { FileObject } from '../dff-analyse/dff-analyse';
import { MainMenuComponent } from '../menu/main-menu';
import { ToolbarAnalyse } from '../toolbar/toolbar-analyse-component';
import { Toolbar } from '../toolbar/toolbar-component';
import { Tree } from '../tree/tree';
import { sizeDisplay, PathUtils } from '../utils';

import './space-usage-analyse.scss';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';

export interface SpaceAnalyseProps {
  match: any;
  location: any;
}

export const SpaceUsageAnalyse: React.FC<SpaceAnalyseProps> = ({
  match,
  location,
}) => {
  const query = queryString.parse(location.search);
  const normalizedQueryPath = query.path[0] !== '/' ? `/${query.path}` : query.path;
  const mountId = match.params.mountId;
  const rootPath = PathUtils.normalize((normalizedQueryPath || '/') as string);

  const [mount, setMount] = useState('');
  const [errorText, setErrorText] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [sortColumn, setSortColumn] = useState('size');
  const [sortReverse, setSortReverse] = useState(true);

  const [root, setRoot] = useState<FileObject>({
    file: {
      name: '',
      type: 'dir',
      size: 0,
      modified: 0,
      hash: '',
      tags: {},
      contentType: '',
      isSelected: false,
    },
    path: rootPath,
    splittedPath: [],
    mountId: mountId,
    children: [],
    remaining: 0,
    descendants: 0,
    size: 0,
  });

  useEffect(() => {
    fetch(`/api/v2/mounts/${mountId}`)
      .then(results => results.json())
      .then(data => {
        setMount(data.name);
      });
  }, [mountId]);

  const buildTree = async (abortSignal: AbortSignal) => {
    const response = await fetch(
      `/content/api/v2/mounts/${mountId}/files/listrecursive?path=${encodeURIComponent(
        rootPath,
      )}`,
      { signal: abortSignal },
    );
    if (response.status !== 200) {
      // TODO display error
      setErrorText(response.statusText);
      return;
    }

    const responseText = await response.text();

    const fileLines = responseText.split('\n').filter(line => line !== '');

    const fetchedFiles = fileLines
      .map((res: string) => {
        const file: api.FileResponse = JSON.parse(res);
        if (file.error != null) {
          return undefined;
        }
        if (file.file.size === -1) {
          // TODO display this
          file.file.size = 0;
        }
        const filePath = PathUtils.joinPaths(rootPath, file.path);
        const mappedFile: FileObject = {
          file: file.file,
          mountId: mountId,
          path: filePath,
          splittedPath: filePath.split('/').filter(c => c !== ''),
          children: [],
          remaining: file.file.type === 'dir' ? 1 : 0,
          descendants: 0,
          size: file.file.size,
          modified: file.file.modified
        };
        return mappedFile;
      })
      .filter(file => file !== undefined) as FileObject[];

    return process(fetchedFiles);
  };

  useEffect(() => {
    // abortController is used for aborting this hook on unmount
    const abortController = new AbortController();

    buildTree(abortController.signal);

    return function cleanup() {
      abortController.abort();
    };
  }, [mountId, rootPath]);

  async function process(fetchedFiles: FileObject[]) {
    const filesMap = new Map(fetchedFiles.map(file => [file.path, file]));

    let lastParentPath: string | undefined = undefined;
    let lastParentPaths: string[] = [];
    let touchedPaths = new Set<string>();

    // skip the root
    const filesToProcess = fetchedFiles.slice(1);

    let currentRemaining = filesToProcess.length;

    const flush = () => {
      touchedPaths.forEach(path => {
        const file = filesMap.get(path);
        file.children = _.reverse(
          _.sortBy(file.children, ['size', 'file.name']),
        );
      });

      const root = filesMap.get(rootPath);
      setRoot(root);
      setRemaining(currentRemaining);

      touchedPaths.clear();
    };

    let lastSleep = Date.now();
    let lastFlush = Date.now();

    const refresh = async () => {
      const now = Date.now();

      // yield every 15 ms
      if (now - lastSleep > 15) {
        lastSleep = now;
        await new Promise(resolve => setTimeout(resolve, 1));
      }

      // flush every 200 ms
      if (now - lastFlush > 200) {
        lastFlush = now;
        flush();
      }
    };

    flush();

    for (const file of filesToProcess) {
      currentRemaining--;

      // e.g. /foo/bar/baz -> /foo/bar
      const parentPath = PathUtils.parent(file.path);

      // e.g. /prefix/foo/bar -> [/prefix, /prefix/foo, /prefix/foo/bar]
      const parentPaths =
        parentPath === lastParentPath
          ? lastParentPaths
          : PathUtils.chainTo(parentPath, rootPath);

      // if lastParentPaths was [/prefix, /prefix/foo, /prefix/foo/bar, /prefix/foo/bar/baz]
      // and parentPaths is [/prefix, /prefix/next]
      // we need to set [/prefix/foo, /prefix/foo/bar, /prefix/foo/bar/baz] as done
      if (parentPaths.length < lastParentPaths.length) {
        lastParentPaths.slice(parentPaths.length).forEach(path => {
          filesMap.get(path).remaining = 0;
        });
      }

      const parent = filesMap.get(parentPath);
      parent.children.push(file);
      file.parent = parent;

      if (file.file.type === 'file') {
        const size = file.file.size;

        parentPaths.forEach(path => {
          touchedPaths.add(path);

          const f = filesMap.get(path);
          f.descendants += 1;
          f.size += size;

          if (f.modified < file.file.modified)
            f.modified = file.file.modified;
        });
      }

      lastParentPath = parentPath;
      lastParentPaths = parentPaths;

      await refresh();
    }

    filesMap.forEach(file => {
      file.remaining = 0;
    });

    flush();

    setIsDone(true);
  }

  const sortByName = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'name') {
      setSortReverse(true);
      newSort = true;
      setSortColumn('name');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }
    sort(file, ['file.name'], newSort);
    setRoot(file);
  };

  const sortByFiles = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'files') {
      setSortReverse(true);
      newSort = true;

      setSortColumn('files');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }
    sort(file, ['descendants'], newSort);
    setRoot(file);
  };

  const sortBySize = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'size') {
      setSortReverse(true);
      newSort = true;

      setSortColumn('size');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }

    sort(file, ['size'], newSort);
    setRoot(file);
  };

  const sortByModified = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'modified') {
      setSortReverse(true);
      newSort = true;

      setSortColumn('modified');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }

    sort(file, ['modified'], newSort);
    setRoot(file);
  };

  const sort = (
    file: FileObject,
    columns: string | string[],
    sortReverseInit: boolean,
  ) => {
    if (file.children && file.children.length) {
      file.children = _.sortBy(file.children, columns);
      if (sortReverseInit) {
        file.children.reverse();
      }
      file.children.forEach(child => {
        sort(child, columns, sortReverseInit);
      });
    }
  };

  const toolbarText = () => {
    return !isDone
      ? `${mount} ${rootPath} | Loading... Remaining: ${remaining}`
      : `${mount} ${rootPath}`;
  };

  const BottomBar = () => {
    return (
      <div className="bottom-bar">
        <div className="bottom-bar__text">
          <p>{`Total size: ${sizeDisplay(root.size)} | Total files: ${
            root.descendants
          }`}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-usage-analyse-container">
      <Toolbar>
        <div className='l-toolbar__column'>
          <Link to="/">
            <img src={spaceLogo} alt="Koofr space usage logo" className="l-toolbar__logo"/>
          </Link>
          <div className='divider'></div>
          <div className='app-name'>Space usage</div>
        </div>
        <div className='l-toolbar__column stretch'>{toolbarText()}</div>
        <div className='l-toolbar__column'>
          <Link to="/space-usage/" className='new-search'>New Search</Link>
          <div className='divider'></div>
          <MainMenuComponent/>
        </div>
      </Toolbar>
      <div className="space-usage-analyse">
        <div className="main-container">
          <div>
            {errorText ? (
              <h1>{errorText}</h1>
            ) : (
                <div className="main-table">
                  <table className="main-table-head">
                    <thead>
                      <tr>
                        <td
                          className={classNames(
                            'head-column',
                            'col-name',
                            'sort-label',
                            {
                              'sort-by sort-by-rev':
                                sortColumn === 'name' && sortReverse,
                              'sort-by': sortColumn === 'name' && !sortReverse,
                            },
                          )}
                        >
                          <span onClick={() => sortByName(root)}>Name</span>
                        </td>
                        <td className="head-column col-space">Space usage</td>
                        <td
                          className={classNames(
                            'head-column',
                            'col-size',
                            'sort-label',
                            {
                              'sort-by sort-by-rev':
                                sortColumn === 'size' && sortReverse,
                              'sort-by': sortColumn === 'size' && !sortReverse,
                            },
                          )}
                        >
                          <span onClick={() => sortBySize(root)}>Size</span>
                        </td>
                        <td
                          className={classNames(
                            'head-column',
                            'col-files',
                            'sort-label',
                            {
                              'sort-by sort-by-rev':
                                sortColumn === 'files' && sortReverse,
                              'sort-by': sortColumn === 'files' && !sortReverse,
                            },
                          )}
                        >
                          <span onClick={() => sortByFiles(root)}>Files</span>
                        </td>
                        <td
                          className={classNames(
                            'head-column',
                            'col-modified',
                            'sort-label',
                            {
                              'sort-by sort-by-rev':
                                sortColumn === 'modified' && sortReverse,
                              'sort-by': sortColumn === 'modified' && !sortReverse,
                            },
                          )}
                        >
                          <span onClick={() => sortByModified(root)}>Modified</span>
                        </td>
                      </tr>
                    </thead>
                  </table>

                  <Tree file={root} />
                </div>
              )}
          </div>
          {!root.descendants && isDone && root ? (
            <div>
              According to our careful calculations, this folder is empty.
            </div>
          ) : null}
          <BottomBar />
        </div>
      </div>
      {!isDone ? (
        <LoadingSpinner />
      ) : null}
    </div>
  );
};
